import { browserTracingIntegration } from "@sentry/browser";
import * as Sentry from "@sentry/react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";

import NeutralinoShortcuts from "../desktop/NeutralinoShortcuts";
import NeutralinoUpdater from "../desktop/NeutralinoUpdater";
import { useIsDesktop } from "../hooks/useIsDesktop";
import { DesktopApp } from "./Auth/DesktopApp";
import { UnifiedAuthProvider } from "./Auth/UnifiedAuthProvider";
import { WebApp } from "./Auth/WebApp";
import { NeutralinoProvider } from "./Neutralino/NeutralinoContextProvider";

const queryClient = new QueryClient();
const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;

Sentry.init({
  dsn: SENTRY_DSN,
  integrations: [browserTracingIntegration()],
  tracesSampleRate: 1.0,
});

export interface ErrorFallbackProps {
  error: unknown;
}
function ErrorFallback({ error }: Readonly<ErrorFallbackProps>) {
  const errorMessage =
    error instanceof Error ? error.message : "An unknown error occurred";

  return (
    <div className="flex min-h-screen items-center justify-center p-4">
      <Card className="w-full max-w-md p-8">
        <h1 className="mb-4 text-2xl font-semibold">
          Sorry, something went wrong
        </h1>
        <p className="mb-8 text-muted-foreground leading-relaxed">
          <span className="font-medium">Error:</span> {errorMessage}
        </p>
        <Button onClick={() => window.location.reload()} className="w-full">
          Reload
        </Button>
      </Card>
    </div>
  );
}

export function App() {
  const isDesktop = useIsDesktop();

  return (
    <Sentry.ErrorBoundary fallback={ErrorFallback}>
      <NeutralinoProvider>
        <QueryClientProvider client={queryClient}>
          <UnifiedAuthProvider>
            {isDesktop ? (
              <>
                <DesktopApp />
                <NeutralinoUpdater />
                <NeutralinoShortcuts />
              </>
            ) : (
              <WebApp />
            )}
          </UnifiedAuthProvider>
        </QueryClientProvider>
      </NeutralinoProvider>
    </Sentry.ErrorBoundary>
  );
}
